/* Copyright 2014-2018 The MathWorks, Inc. */

define([
    "dojo/_base/declare",
    "dojo/_base/lang",
    "dijit/registry",
    "dojo/dom-class",
    "MW/uiframework/UIBuilderFactory",
    "MW/uiframework/contextmanager/FeatureManager"
], function (declare, lang, registry, domClass, UIBuilderFactory, FeatureManager) {
    return declare([FeatureManager], {

        constructor: function (args) {
            this._toolstripId = args.contextManagerImpl.toolstripId;
            this._toolstripWidget = registry.byId(this._toolstripId);

            this._registeredTabGroups = {}; // The map of all registered tab groups for different contexts
            this._lastValidContext = null;
            this._lastSelectedTabId = null;
            this._showingContextualTabGroups = [];

            this._handles.push(this._uiBuilder.addPropertySetCallback(this._toolstripId, lang.hitch(this, this._selectedTabChangedCallback)));
        },

        registerTabGroups: function (args) {
            if (!this._registeredTabGroups[args.appId]) {
                this._registeredTabGroups[args.appId] = {};
            }

            if (!this._registeredTabGroups[args.appId][args.contextId]) {
                this._registeredTabGroups[args.appId][args.contextId] = {};
            }

            this._registeredTabGroups[args.appId][args.contextId].tabGroupTags = args.tabGroupTags;

            // Add the global tab groups of the parent app as soon as they are registered
            if (args.appId === this._contextManagerImpl.parentAppId && args.contextId === "global") {
                this._addGlobalTabGroups();
            }
        },

        unregisterTabGroups: function (args) {
            if (this._registeredTabGroups[args.appId]) {
                if (this._registeredTabGroups[args.appId][args.contextId]) {
                    delete this._registeredTabGroups[args.appId][args.contextId];
                } else {
                    delete this._registeredTabGroups[args.appId];
                }
            }
        },

        resetLastSelectedTab: function (args) {
            if (this._registeredTabGroups[args.appId]) {
                if (this._registeredTabGroups[args.appId][args.contextId]) {
                    this._registeredTabGroups[args.appId][args.contextId].lastSelectedTabId = null;
                } else {
                    Object.keys(this._registeredTabGroups[args.appId]).forEach(function (contextId) {
                        this._registeredTabGroups[args.appId][contextId].lastSelectedTabId = null;
                    }, this);
                }
            }
        },

        _update: function (context, secondaryContext) {

            var updatedContextualTabGroups = this._getTabGroupsForContext(context, secondaryContext);
            if (JSON.stringify(this._showingContextualTabGroups) !== JSON.stringify(updatedContextualTabGroups)) {

                this._previouslySelectedTabId = this._uiBuilder.get(this._toolstripId, "selectedTab");

                // If the app being set into context has no registered tab groups then don't proceed
                if (context && this._registeredTabGroups[context.appId]) {

                    this._lastValidContext = this._contextManagerImpl.get("currentContext");

                    // Take a snapshot of this state of the registered tab groups map of each context of the new app since
                    // the last selected tab gets altered while adding/removing tabs
                    this._registeredTabGroupsForNewApp = lang.clone(this._registeredTabGroups[context.appId]);
                }

                this._removeContextualTabGroups(this._showingContextualTabGroups);
                this._addContextualTabGroups(updatedContextualTabGroups);
                this._showingContextualTabGroups = updatedContextualTabGroups;

                // Select the appropriate tab
                if (this._contextManagerImpl.alternativeContextMemory) {
                    this._selectAppropriateTab(context.appId);
                } else if (context && context.contextIdList && this._showingContextualTabGroups.length > 0) {
                    context.contextIdList.forEach(function (contextId) {
                        this._selectAppropriateTab(context.appId, contextId);
                    }, this);
                } else if (this._contextManagerImpl.parentAppId) {
                    this._selectAppropriateTab(this._contextManagerImpl.parentAppId, "global");
                }
            }
        },

        _getTabGroupsForContext: function (context, secondaryContext) {
            var  tabGroupTags = [];
            // Add the tab groups of all the contextIds in the contextIdList
            if (context && context.contextIdList &&  this._registeredTabGroups[context.appId]) {
                context.contextIdList.forEach(function (contextId) {
                    // Don't need to add the global tab groups of the parent app since they are never removed
                    if (!(context.appId === this._contextManagerImpl.parentAppId && contextId === "global") &&
                        this._registeredTabGroups[context.appId][contextId] &&
                        this._registeredTabGroups[context.appId][contextId].tabGroupTags) {
                        tabGroupTags = tabGroupTags.concat(this._registeredTabGroups[context.appId][contextId].tabGroupTags);
                    }
                }, this);
            }
            // Add tab groups associated with the secondary context
            if (secondaryContext && secondaryContext.contextIdList && this._registeredTabGroups[secondaryContext.appId]) {
                secondaryContext.contextIdList.forEach(function (contextId) {
                    if (this._registeredTabGroups[secondaryContext.appId][contextId] &&
                        this._registeredTabGroups[secondaryContext.appId][contextId].tabGroupTags) {
                        tabGroupTags = tabGroupTags.concat(this._registeredTabGroups[secondaryContext.appId][contextId].tabGroupTags);
                    }
                }, this);
            }
            return tabGroupTags;
        },

        _removeContextualTabGroups: function(tabGroupTags) {
            this._ignoreSelectedTabChange = true;
            this._toolstripWidget._ignoreResize = true;
            tabGroupTags.forEach(function (childTag) {
                this._uiBuilder.remove(this._uiBuilder.tagToId(childTag));
            }, this);
            this._ignoreSelectedTabChange = false;
            this._toolstripWidget._ignoreResize = false;
        },

        _addContextualTabGroups: function(tabGroupTags) {
            this._ignoreSelectedTabChange = true;
            this._toolstripWidget._ignoreResize = true;

            tabGroupTags.forEach(function (tabGroupTag) {
                var tabGroupId = this._uiBuilder.tagToId(tabGroupTag);
                this._uiBuilder.add(tabGroupId, this._toolstripId);

                // Apply CSS to differentiate contextual tabs from global tabs
                var tabs = this._uiBuilder.getChildrenIds(tabGroupId);
                tabs.forEach(function (tab) {
                    domClass.add(registry.byId(tab).controlButton.domNode, "toolstripContextualTab");
                });

            }, this);
            this._ignoreSelectedTabChange = false;
            this._toolstripWidget._ignoreResize = false;
        },

        _addGlobalTabGroups: function () {
            var appId = this._contextManagerImpl.parentAppId;
            var contextId = "global";
            if (this._registeredTabGroups[appId] && this._registeredTabGroups[appId][contextId]) {
                // Add all the registered tab groups for the given context
                this._ignoreSelectedTabChange = true;
                this._toolstripWidget._ignoreResize = true;
                this._registeredTabGroups[appId][contextId].tabGroupTags.forEach(function (tabGroupTag) {
                    var groupId = this._uiBuilder.tagToId(tabGroupTag);
                    if (this._uiBuilder.isAnOrphan(groupId)) {
                        this._uiBuilder.add(groupId, this._toolstripId);
                    }
                }, this);
                this._ignoreSelectedTabChange = false;
                this._toolstripWidget._ignoreResize = false;
            }
        },

        _selectAppropriateTab: function (appId, contextId) {

            if (this._registeredTabGroups[appId]) {
                if (this._contextManagerImpl.alternativeContextMemory) {

                    if (this._lastSelectedTabId && !this._uiBuilder.isAnOrphan(this._lastSelectedTabId)) {
                        this._uiBuilder.set(this._toolstripId, "selectedTab", "");
                        this._uiBuilder.set(this._toolstripId, "selectedTab", this._lastSelectedTabId);
                    } else if (this._registeredTabGroups[appId]["global"] && this._registeredTabGroups[appId]["global"].tabGroupTags && this._registeredTabGroups[appId]["global"].tabGroupTags.length > 0) {
                        this._uiBuilder.selectFirstTabInTabGroup(this._registeredTabGroups[appId]["global"].tabGroupTags[0], this._toolstripId);
                    }
                } else {
                    if (this._registeredTabGroups[appId][contextId]) {

                        var lastSelectedTabId = this._registeredTabGroupsForNewApp && this._registeredTabGroupsForNewApp[contextId].lastSelectedTabId;

                        // If there is no last selected tab remembered for the new context
                        if (!lastSelectedTabId || this._uiBuilder.isAnOrphan(lastSelectedTabId)) {
                            // Then select the first tab in the first registered tab group of the context
                            if (this._registeredTabGroups[appId] && this._registeredTabGroups[appId][contextId] && this._registeredTabGroups[appId][contextId].tabGroupTags && this._registeredTabGroups[appId][contextId].tabGroupTags.length > 0) {
                                this._uiBuilder.selectFirstTabInTabGroup(this._registeredTabGroups[appId][contextId].tabGroupTags[0], this._toolstripId);
                            }
                            // If the previously selected tab is a global tab
                        } else if (this._isAGlobalTab(this._previouslySelectedTabId, appId)) {
                            // And the last selected tab is also a global tab
                            if (this._isAGlobalTab(lastSelectedTabId, appId)) {
                                // Then just select the previously selected global tab
                                this._uiBuilder.set(this._toolstripId, "selectedTab", this._previouslySelectedTabId);
                                // If the last selected tab is not a global tab
                            } else {
                                // Then select the last selected tab stored with the context
                                this._uiBuilder.set(this._toolstripId, "selectedTab", lastSelectedTabId);
                            }
                            // If there is a last selected tab and the previously selected tab is not a global tab
                        } else {
                            // Then select the last selected tab stored with the context
                            this._uiBuilder.set(this._toolstripId, "selectedTab", "");
                            this._uiBuilder.set(this._toolstripId, "selectedTab", lastSelectedTabId);
                        }
                    }
                }
            }
        },

        _selectedTabChangedCallback: function (eventData) {
            if (!this._ignoreSelectedTabChange && eventData.property === "selectedTab") {
                if (this._contextManagerImpl.alternativeContextMemory) {
                    this._lastSelectedTabId = eventData.newValue;
                } else {
                    if (!this._lastValidContext || !this._registeredTabGroups[this._lastValidContext.appId] || !this._lastValidContext.contextIdList) {
                        return;
                    }

                    this._lastValidContext.contextIdList.forEach(function (contextId) {
                        if (this._registeredTabGroups[this._lastValidContext.appId][contextId]) {
                            if (this._registeredTabGroups[this._lastValidContext.appId][contextId].tabGroupTags) {
                                this._registeredTabGroups[this._lastValidContext.appId][contextId].lastSelectedTabId = eventData.newValue;
                            }
                        }
                    }, this);
                }
            }
        },

        _isAGlobalTabGroupOfParentApp: function (tabGroupId) {
            var result = false, tabGroupTag = this._uiBuilder.get(tabGroupId, "tag");

            if (this._contextManagerImpl.parentAppId) {
                if (this._registeredTabGroups[this._contextManagerImpl.parentAppId]) {
                    if (this._registeredTabGroups[this._contextManagerImpl.parentAppId]["global"]) {
                        if (this._registeredTabGroups[this._contextManagerImpl.parentAppId]["global"].tabGroupTags) {
                            result = this._registeredTabGroups[this._contextManagerImpl.parentAppId]["global"].tabGroupTags.indexOf(tabGroupTag) > -1;
                        }
                    }
                }
            }

            return result;
        },

        _isAGlobalTab: function (tabId, appId) {
            var result = false;

            if (this._contextManagerImpl.parentAppId) {
                result = this._isAChildOfRegisteredTabGroups(tabId, this._contextManagerImpl.parentAppId, "global");
            } else {
                result = this._isAChildOfRegisteredTabGroups(tabId, appId, "global");
            }

            return result;
        },

        _isAChildOfRegisteredTabGroups: function (tabId, appId, contextId) {
            var result = false, parentTabGroupId, parentTabGroupTag;

            if (this._registeredTabGroups[appId][contextId].tabGroupTags) {
                parentTabGroupId = this._uiBuilder.getParentId(tabId);
                parentTabGroupTag = this._uiBuilder.get(parentTabGroupId, "tag");

                // If the parent tab group is one of the registered tab groups then return true;
                if (this._registeredTabGroups[appId][contextId].tabGroupTags.indexOf(parentTabGroupTag) > -1) {
                    result = true;
                }
            }

            return result;
        }
    });
});
